import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import store from '.';
import { TButtonFormAction, TFormScriptEvents } from '../types';
import { handleDeadSession } from '../components/apia/main/TabsList/ContentRenderer';

export type TFormScriptEventNames =
  | ''
  | 'onLoad-scripts-ready'
  | 'on-onLoad-scripts-done'
  | 'onReload-scripts-ready'
  | 'on-onReload-scripts-done'
  | 'onSubmit-scripts-ready'
  | 'on-onSubmit-scripts-done'
  | null;

export type TFormState = {
  form: {
    id: string;
    index: number;
    event: TFormScriptEventNames;
    scriptEvents?: TFormScriptEvents;
    scriptErrors?: boolean;
    error?: boolean;
    frmName?: string;
    frmParent?: string;
  };
};

export interface IAppState {
  actionState: TActionState;
  prevActionState: TActionState;
  actionGoto: TActionGotoState;
  actionEvent: TActionEvent;
  sessionState: boolean;
  invalidValues: string[];
  message: string;
  formState: TFormState[];
}

export type TActionEvent =
  | 'form-on-submit-event'
  | 'form-on-submit-event-done'
  | 'form-on-validation'
  | 'form-on-revalidate'
  | 'form-on-sync-fields'
  | 'form-on-check-web-dav'
  | 'form-on-next'
  | 'form-on-error'
  | 'form-on-validate-tabs'
  | 'form-on-force-confirm'
  | null;
export type TActionState = TButtonFormAction | 'invalid' | null;
export type TActionGotoState = string | null;
export type TActionSession = {
  isLog: boolean;
  message: string;
};

const updateFormEvent = (
  currentFormState: TFormState[],
  index: number,
  event: TFormScriptEventNames,
  error: boolean,
  scriptErrors: boolean,
): TFormState[] => {
  const updatedEventList = currentFormState.map((el) =>
    el.form.index === index
      ? { ...el, form: { ...el.form, event, error, scriptErrors } }
      : el,
  );
  return updatedEventList;
};

const appStateSlice = createSlice({
  name: 'appStateSlice',
  initialState: {
    actionState: null,
    prevActionState: null,
    actionGoto: null,
    sessionState: true,
    actionEvent: null,
    message: '',
    invalidValues: [],
    formState: [],
  } as IAppState,
  reducers: {
    updateActionState: (state, action: PayloadAction<TActionState>) => {
      state.actionState = action.payload;
      if (action.payload === null) {
        state.actionEvent = null;
      } else {
        state.prevActionState = action.payload;
      }
    },
    updateActionEvent: (state, action: PayloadAction<TActionEvent>) => {
      state.actionEvent = action.payload;
      if (action.payload === 'form-on-error') {
        state.actionState = null;
      }
      if (action.payload === 'form-on-submit-event-done') {
        state.actionEvent = 'form-on-revalidate';
      }
    },
    updateSessionState: (state, action: PayloadAction<TActionSession>) => {
      window.top?.closeSession?.(false);
      if (!window.top?.closeSession) handleDeadSession(false);
      state.sessionState = action.payload.isLog;
      state.message = action.payload.message;
    },
    setGotoState: (state, action: PayloadAction<TActionGotoState>) => {
      state.actionGoto = action.payload;
    },
    setActionFormSubmitState: (state, action: PayloadAction<TActionState>) => {
      state.actionState = action.payload;
      state.actionEvent = null;
    },
    updateInvalidValues: (state, action: PayloadAction<string[]>) => {
      state.invalidValues = action.payload;
    },
    setFormState: (state, action: PayloadAction<TFormState>) => {
      state.formState.push(action.payload);
    },
    updateFormEventState: (
      state,
      action: PayloadAction<{
        index: number;
        event: TFormScriptEventNames;
        scriptErrors: boolean;
        error: boolean;
      }>,
    ) => {
      state.formState = updateFormEvent(
        state.formState,
        action.payload.index,
        action.payload.event,
        action.payload.error,
        action.payload.scriptErrors,
      );
    },
  },
});

export default appStateSlice.reducer;
export const {
  setGotoState,
  setActionFormSubmitState,
  updateSessionState,
  updateInvalidValues,
  setFormState,
  updateFormEventState,
} = appStateSlice.actions;

export function updateActionEvent(event: TActionEvent) {
  store.dispatch(appStateSlice.actions.updateActionEvent(event));
}

export function updateActionState(state: TActionState) {
  store.dispatch(appStateSlice.actions.updateActionState(state));
}
